import { Filter, Search, SlidersHorizontal } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@vnetwork/ui/primitives';
import { FilterBuilder, type FilterField, type FilterCondition } from '@vnetwork/ui/data';

const FILTER_FIELDS: FilterField[] = [
  {
    value: 'business_type',
    label: 'Type',
    input: 'select',
    options: [
      { value: 'meeting', label: 'Meeting' },
      { value: 'datacenter', label: 'Datacenter' },
      { value: 'orthers', label: 'Others' },
    ],
  },
  {
    value: 'business_status',
    label: 'Status',
    input: 'select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'leader-approved', label: 'Leader approved' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
  },
];

const TOGGLEABLE_COLUMNS = [
  { id: 'business_type', label: 'Type' },
  { id: 'business_dates', label: 'Business date' },
  { id: 'business_place', label: 'Location' },
  { id: 'business_leader_status', label: 'Leader' },
  { id: 'business_bod_status', label: 'BOM/BOD' },
  { id: 'business_created_at', label: 'Created' },
];

export interface BusinessesAdminToolbarProps {
  query: string;
  onQueryChange: (value: string) => void;
  conditions: FilterCondition[];
  filterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  onApplyFilters: (next: FilterCondition[]) => void;
  /** Optional: column visibility toggle state + setter. */
  visible?: Record<string, boolean>;
  onToggleColumn?: (id: string, checked: boolean) => void;
}

/**
 * Toolbar for the HRM businesses admin list: search + Filters popover
 * (type, status) + Display-options column visibility toggle.
 */
export function BusinessesAdminToolbar({
  query,
  onQueryChange,
  conditions,
  filterOpen,
  onFilterOpenChange,
  onApplyFilters,
  visible = {},
  onToggleColumn,
}: BusinessesAdminToolbarProps) {
  const appliedCount = new Set(conditions.map((c) => c.field)).size;

  return (
    <Toolbar
      style={{ marginBottom: 0 }}
      start={
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search by employee, location, content…"
          startSlot={<Search size={15} />}
          style={{ maxWidth: 320 }}
        />
      }
      end={
        <>
          <Popover open={filterOpen} onOpenChange={onFilterOpenChange}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{' '}
                {appliedCount ? <Badge tone="primary">{appliedCount}</Badge> : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={FILTER_FIELDS}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={onApplyFilters}
                onClose={() => onFilterOpenChange(false)}
              />
            </PopoverContent>
          </Popover>

          {onToggleColumn && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE_COLUMNS.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.id}
                    checked={visible[col.id] !== false}
                    onCheckedChange={(v) => onToggleColumn(col.id, v === true)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      }
    />
  );
}
